import * as i0 from '@angular/core';
import { inject, NgZone, TemplateRef, Component, Input, EventEmitter, ElementRef, Directive, Output, HostListener, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { Overlay, CdkOverlayOrigin, OverlayModule } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { hasTrigger, getOverlay, positions, getPositionStrategy } from '@simpl/element-ng/common';

class PopoverComponent {
    popover;
    popoverTitle = '';
    anchor;
    positions;
    containerClass = '';
    icon;
    popoverPositionClass = '';
    left;
    right;
    popoverTemplate = null;
    zone = inject(NgZone);
    ngOnInit() {
        if (this.popover instanceof TemplateRef) {
            this.popoverTemplate = this.popover;
        }
    }
    updateArrow(change) {
        let popoverClassTooltip = `popover-${change.connectionPair.overlayX}-${change.connectionPair.overlayY}`;
        if (this.containerClass) {
            popoverClassTooltip += ' ' + this.containerClass;
        }
        const offsetX = change.connectionPair.offsetX;
        if (offsetX) {
            if (offsetX < 0) {
                // Need to subtract half of the width of arrow to get the center
                this.left = -1 * (offsetX + 6);
            }
            else {
                // Need to subtract half of the width of arrow to get the center
                this.right = offsetX - 6;
            }
        }
        if (popoverClassTooltip !== this.popoverPositionClass) {
            this.zone.run(() => (this.popoverPositionClass = popoverClassTooltip));
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: PopoverComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: PopoverComponent, selector: "si-popover", inputs: { popover: "popover", popoverTitle: "popoverTitle", anchor: "anchor", positions: "positions", containerClass: "containerClass", icon: "icon" }, ngImport: i0, template: "<div class=\"popover position-relative\" [ngClass]=\"popoverPositionClass\">\n  <div class=\"popover-arrow flip-rtl\" [style.left.px]=\"left\" [style.right.px]=\"right\"></div>\n  <div *ngIf=\"popoverTitle\" class=\"popover-header\">\n    <span *ngIf=\"icon\" class=\"icon me-2\" [ngClass]=\"icon\"></span>\n    <span class=\"si-title-2\">{{ popoverTitle }}</span>\n  </div>\n  <div class=\"popover-body\">\n    <div *ngIf=\"!popoverTemplate; else templateRef\" class=\"si-body-2\" [innerHTML]=\"popover\"></div>\n    <ng-template #templateRef>\n      <ng-template *ngTemplateOutlet=\"popoverTemplate\"></ng-template>\n    </ng-template>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: PopoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-popover', template: "<div class=\"popover position-relative\" [ngClass]=\"popoverPositionClass\">\n  <div class=\"popover-arrow flip-rtl\" [style.left.px]=\"left\" [style.right.px]=\"right\"></div>\n  <div *ngIf=\"popoverTitle\" class=\"popover-header\">\n    <span *ngIf=\"icon\" class=\"icon me-2\" [ngClass]=\"icon\"></span>\n    <span class=\"si-title-2\">{{ popoverTitle }}</span>\n  </div>\n  <div class=\"popover-body\">\n    <div *ngIf=\"!popoverTemplate; else templateRef\" class=\"si-body-2\" [innerHTML]=\"popover\"></div>\n    <ng-template #templateRef>\n      <ng-template *ngTemplateOutlet=\"popoverTemplate\"></ng-template>\n    </ng-template>\n  </div>\n</div>\n" }]
        }], propDecorators: { popover: [{
                type: Input
            }], popoverTitle: [{
                type: Input
            }], anchor: [{
                type: Input
            }], positions: [{
                type: Input
            }], containerClass: [{
                type: Input
            }], icon: [{
                type: Input
            }] } });

class SiPopoverDirective {
    /**
     * The popover text to be displayed
     */
    siPopover;
    _placement;
    /**
     * The placement of the popover. One of 'top', 'start', end', 'bottom'
     */
    set placement(value) {
        const deprecatedMessage = '@simpl/element-ng - SiPopoverDirective: \
Setting placement to "left" or "right" is deprecated, use "start" or "end" instead';
        if (value === 'left') {
            console.warn(deprecatedMessage);
            this._placement = 'start';
        }
        else if (value === 'right') {
            console.warn(deprecatedMessage);
            this._placement = 'end';
        }
        else {
            this._placement = value;
        }
    }
    get placement() {
        return this._placement;
    }
    /**
     * The trigger event on which the popover shall be displayed
     */
    triggers = 'click';
    /**
     * The title to be displayed on top for the popover
     */
    popoverTitle = '';
    /**
     * The class that will be applied to container of the popover
     */
    containerClass = '';
    /**
     * The flag determines whether to close popover on clicking outside
     */
    outsideClick = true;
    /**
     * The icon to be displayed besides popover title
     */
    icon;
    /**
     * Specify whether or not the popover is currently shown
     */
    isOpen = false;
    /**
     * Emits an event when the popover is shown
     */
    shown = new EventEmitter();
    /**
     * Emits an event when the popover is hidden
     */
    hidden = new EventEmitter();
    overlayref;
    overlay = inject(Overlay);
    elementRef = inject(ElementRef);
    ngOnInit() {
        if (this.placement !== 'top' &&
            this.placement !== 'bottom' &&
            this.placement !== 'start' &&
            this.placement !== 'end') {
            this.placement = 'auto';
        }
        if (this.isOpen) {
            this.show();
        }
    }
    ngOnDestroy() {
        this.overlayref?.dispose();
    }
    /**
     * Displays popover and emits 'shown' event.
     */
    show() {
        if (!this.overlayref?.hasAttached()) {
            const backdrop = this.outsideClick &&
                !hasTrigger('focus', this.triggers) &&
                !hasTrigger('hover', this.triggers);
            this.overlayref = getOverlay(this.elementRef, this.overlay, backdrop, this.placement);
            if (backdrop) {
                this.overlayref.backdropClick().subscribe(() => this.hide());
            }
        }
        if (this.overlayref.hasAttached()) {
            return;
        }
        const popoverPortal = new ComponentPortal(PopoverComponent);
        const popoverRef = this.overlayref.attach(popoverPortal);
        popoverRef.instance.popover = this.siPopover;
        popoverRef.instance.popoverTitle = this.popoverTitle;
        popoverRef.instance.anchor = new CdkOverlayOrigin(this.elementRef);
        popoverRef.instance.positions = positions[this.placement];
        popoverRef.instance.icon = this.icon;
        popoverRef.instance.containerClass = this.containerClass;
        const positionStrategy = getPositionStrategy(this.overlayref);
        positionStrategy?.positionChanges.subscribe(change => popoverRef.instance.updateArrow(change));
        popoverRef.changeDetectorRef.detectChanges();
        this.shown.emit();
    }
    /**
     * Hides the popover and emits 'hidden' event.
     */
    hide() {
        this.overlayref?.detach();
        this.hidden.emit();
    }
    /**
     * Updates the position of the popover based on the position strategy.
     */
    updatePosition() {
        this.overlayref?.updatePosition();
    }
    onTrigger(trigger) {
        if (hasTrigger(trigger, this.triggers)) {
            if (this.overlayref?.hasAttached()) {
                this.hide();
            }
            else {
                this.show();
            }
        }
    }
    focusOut() {
        if (hasTrigger('focus', this.triggers)) {
            if (this.outsideClick) {
                this.hide();
            }
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiPopoverDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiPopoverDirective, selector: "[siPopover]", inputs: { siPopover: "siPopover", placement: "placement", triggers: "triggers", popoverTitle: "popoverTitle", containerClass: "containerClass", outsideClick: "outsideClick", icon: "icon", isOpen: "isOpen" }, outputs: { shown: "shown", hidden: "hidden" }, host: { listeners: { "mouseenter": "onTrigger(\"hover\")", "mouseleave": "onTrigger(\"hover\")", "focus": "onTrigger(\"focus\")", "click": "onTrigger(\"click\")", "touchstart": "focusOut()", "focusout": "focusOut()" } }, exportAs: ["si-popover"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiPopoverDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siPopover]',
                    exportAs: 'si-popover'
                }]
        }], propDecorators: { siPopover: [{
                type: Input
            }], placement: [{
                type: Input
            }], triggers: [{
                type: Input
            }], popoverTitle: [{
                type: Input
            }], containerClass: [{
                type: Input
            }], outsideClick: [{
                type: Input
            }], icon: [{
                type: Input
            }], isOpen: [{
                type: Input
            }], shown: [{
                type: Output
            }], hidden: [{
                type: Output
            }], onTrigger: [{
                type: HostListener,
                args: ['mouseenter', ['"hover"']]
            }, {
                type: HostListener,
                args: ['mouseleave', ['"hover"']]
            }, {
                type: HostListener,
                args: ['focus', ['"focus"']]
            }, {
                type: HostListener,
                args: ['click', ['"click"']]
            }], focusOut: [{
                type: HostListener,
                args: ['touchstart']
            }, {
                type: HostListener,
                args: ['focusout']
            }] } });

class SiPopoverModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiPopoverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiPopoverModule, declarations: [PopoverComponent, SiPopoverDirective], imports: [CommonModule, OverlayModule], exports: [PopoverComponent, SiPopoverDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiPopoverModule, imports: [CommonModule, OverlayModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiPopoverModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, OverlayModule],
                    declarations: [PopoverComponent, SiPopoverDirective],
                    exports: [PopoverComponent, SiPopoverDirective]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { PopoverComponent, SiPopoverDirective, SiPopoverModule };
